import React from 'react';
import Helmet from 'react-helmet';
import { Redirect } from 'react-router-dom';
import FaiCTA from '../../components/FaiCTA/FaiCTA';
import FundBookFooter from '../../components/FundBookFooter/FundBookFooter';
import FundSnapshot from '../../components/FundSnapshot/FundSnapshot';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import MobileContactHeader from '../../components/MobileContactHeader/MobileContactHeader';
import VariableDisplayEngine from '../../components/VariableDisplayEngine/VariableDisplayEngine';
import LegalReviewNumber from '../../components/LegalReviewNumber/LegalReviewNumber';
import errorMessageIcon from '../../images/error-message.svg';
import { CANONICAL_PATH } from '../../utils/constants';
import { redirectTickerToScreener } from '../../utils/fund-info';
import styles from './FundBook.css';
import { ETF_FUND_TYPE, FUND_TYPE } from './queries';
import { etfTypeUtil, fundTypeUtil } from './util';

const FundBook = ({ match, loggedIn, userStateLoaded, etf, history }) => {
    const { fund } = match.params;

    return (
        <div className="book">
            <MobileContactHeader
                changePhoneNumber
                selectedOption={etf ? 'etf' : 'mf'}
            />
            <div className="fundbook-container">
                {/**
                 *  1. (redirectTickerToScreener) check to see if this ticker is in the list of 'hidden' tickers
                 *  2. Choose a query to use if we have an etf or a MF
                 *  3. Fetch the data from gql
                 *  4. Choose a mutator function based on etf/mf
                 *  5. mutate the returned data AND provide `rawData`
                 *    a. Run biz logic functions against this known ticker
                 *    b. Chooses a VDE template based on ticker
                 */}
                <GqlQueryWrapper
                    query={etf ? ETF_FUND_TYPE : FUND_TYPE}
                    variables={{ ticker: redirectTickerToScreener(fund) }}
                    dataFunc={etf ? etfTypeUtil : fundTypeUtil}
                >
                    {({ data, rawData, error }) => {
                        // maybe I should just change all the namespace to "fund"
                        // and have a util that modifies the response
                        const fundType = etf
                            ? 'exchangeTradedFund'
                            : 'mutualFund';
                        const isFI = data && data.fundType === 'FI_ETF';
                        if (error) {
                            return (
                                <div className="error-container">
                                    <img
                                        className="error-icon"
                                        src={errorMessageIcon}
                                        alt=""
                                    />
                                    <div className="error-header">
                                        We&apos;re sorry, something went wrong
                                        on our side.
                                    </div>
                                    <div className="dash" />
                                    <div className="retry-msg">
                                        Please{' '}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                window.location.reload()
                                            }
                                            className="link"
                                        >
                                            try again
                                        </button>
                                    </div>
                                </div>
                            );
                        }
                        const canonicalSubPath = etf
                            ? `etf/${fund}`
                            : `${fund}`;
                        return data ? (
                            <div>
                                <Helmet>
                                    <title>
                                        {`${
                                            etf
                                                ? rawData[fundType].name
                                                : rawData[fundType].shortName
                                        } (${
                                            rawData[fundType].ticker
                                        }) Investment Performance - T. Rowe Price`}
                                    </title>
                                    <link
                                        rel="canonical"
                                        href={`${CANONICAL_PATH}/${canonicalSubPath}`}
                                    />
                                    <meta
                                        name="title"
                                        content={`${rawData[fundType].name} (${
                                            rawData[fundType].ticker
                                        }) Investment Performance - T. Rowe Price`}
                                    />
                                    <meta
                                        name="author"
                                        content="Trailblazers"
                                    />
                                    <meta
                                        name="description"
                                        content={`Find investment performance and objectives for the ${
                                            rawData[fundType].name
                                        } Fund (${
                                            rawData[fundType].ticker
                                        }) from T. Rowe Price`}
                                    />
                                    <meta
                                        name="keywords"
                                        content={`${
                                            rawData[fundType].name
                                        } Fund, ${rawData[fundType].ticker}`}
                                    />
                                    <meta
                                        name="ticker"
                                        content={rawData[fundType].ticker}
                                    />
                                    <meta
                                        name="robots"
                                        content="index,follow"
                                    />

                                    <meta
                                        name="og:title"
                                        content={`${rawData[fundType].name} (${
                                            rawData[fundType].ticker
                                        }) Investment Performance - T. Rowe Price`}
                                    />
                                    <meta name="og:type" content="website" />
                                    <meta
                                        name="og:url"
                                        content={`https://www.troweprice.com/personal-investing/tools/fund-research/${
                                            rawData[fundType].ticker
                                        }`}
                                    />
                                    <meta
                                        name="og:site_name"
                                        content="T. Rowe Price Fund Information"
                                    />
                                    <meta
                                        name="og:description"
                                        content={`Find investment performance and objectives for the ${
                                            rawData[fundType].name
                                        } Fund (${
                                            rawData[fundType].ticker
                                        }) from T. Rowe Price`}
                                    />
                                </Helmet>
                                <FundSnapshot
                                    {...rawData[fundType]}
                                    {...{
                                        etf,
                                        isFI,
                                        loggedIn,
                                        history,
                                        userStateLoaded
                                    }}
                                />

                                <VariableDisplayEngine
                                    data={data}
                                    etf={etf}
                                    isFI={isFI}
                                    {...rawData[fundType]}
                                />

                                <FaiCTA ticker={fund} />
                                <FundBookFooter
                                    etf={etf}
                                    {...rawData[fundType]}
                                />
                                <LegalReviewNumber
                                    lrnCode={data.ticker}
                                    styleFlags={{ grey: true }}
                                />
                            </div>
                        ) : (
                            <div>
                                <Redirect
                                    to={{
                                        pathname: etf ? '/etf' : '/'
                                    }}
                                />
                            </div>
                        );
                    }}
                </GqlQueryWrapper>
            </div>
            <style jsx>{styles}</style>
        </div>
    );
};
export default FundBook;
